// The IndexedDB-backed filesystem keeps a synchronous in-memory cache per page,
// so two tabs writing the same store would overwrite each other. One tab holds
// the "pi-web-instance" lock while pi runs. Another tab waits for the lock and
// can ask the holder, over a BroadcastChannel, to save and step aside. The lock
// is never stolen: that would drop the holder's unsaved writes.

import type { ExtensionContext, InlineExtension } from "../../upstream/packages/coding-agent/src/core/extensions/types.ts";
import { runningCommands } from "../node/child_process.ts";
import { storageState } from "../storage/state.ts";
import { showPage } from "../ui/page.ts";
import { startupFailed } from "./failure.ts";
import { handOffOnExit, piExited } from "./lifecycle.ts";
import { flushVfs } from "./vfs.ts";

const NAME = "pi-web-instance";
/** How long a waiting tab waits for the first answer. The holder ignores requests older than this. */
const ANSWER_TIMEOUT = 3000;
/** Set by "Use pi here again": ask for pi as soon as this tab has reloaded. */
const ASK_ON_LOAD = "pi-web:take-over";
const TAB_TITLE = "π (open in another tab)";
const SAVE_FAILED = "The other tab could not save its work. Retry saving there, then try again.";

type Message =
	| { type: "request"; id: string; sent: number }
	| { type: "answer"; id: string; state: "saving" | "accepted" | "refused"; reason?: string };
type Release = () => void;

let ctx: ExtensionContext | undefined;
let prompts = 0;

/** Tells the holder whether pi is busy. */
export function createInstanceExtension(): InlineExtension {
	return { name: "pi-web-instance", hidden: true, factory(pi) {
		pi.on("session_start", (_event, next) => { ctx = next; prompts = 0; });
		pi.on("session_shutdown", () => { ctx = undefined; });
		pi.on("ui_prompt_start", () => { prompts++; });
		pi.on("ui_prompt_end", () => { prompts = Math.max(0, prompts - 1); });
	} };
}

/** Why pi cannot leave this tab now, in the words the waiting tab shows. */
function refusal(): string | undefined {
	if (document.querySelector("dialog[open]")) return "The other tab has a dialog open. Close it there, then try again.";
	if (storageState.snapshot().status === "failed") return SAVE_FAILED;
	if (piExited() || startupFailed()) return undefined;
	const starting = "pi is still starting in the other tab. Try again in a moment.";
	if (!ctx) return starting;
	try { if (!ctx.isIdle() || ctx.hasPendingMessages()) return "pi is working on a task in the other tab. Let it finish or stop it there, then try again."; }
	catch { return starting; }
	if (runningCommands()) return "A command is running in the other tab. Let it finish or stop it there, then try again.";
	if (prompts) return "pi is waiting for an answer in the other tab. Answer it there, then try again.";
	return undefined;
}

function request(options: LockOptions): Promise<Release | undefined> {
	return new Promise((resolve, reject) => {
		navigator.locks.request(NAME, options, (lock) => {
			if (!lock) { resolve(undefined); return undefined; }
			// Held until released.
			return new Promise<void>((released) => resolve(() => released()));
		}).catch(reject);
	});
}

/** While pi runs here, answer other tabs' requests to move it. */
function hold(release: Release): void {
	if (typeof BroadcastChannel !== "function") return;
	const channel = new BroadcastChannel(NAME);
	let moving = false;
	channel.onmessage = async ({ data }: MessageEvent<Message>) => {
		// A request sent while this tab was frozen: the asking tab has given up.
		if (data?.type !== "request" || Date.now() - data.sent > ANSWER_TIMEOUT) return;
		const answer = (state: "saving" | "accepted" | "refused", reason?: string) => channel.postMessage({ type: "answer", id: data.id, state, reason } satisfies Message);
		if (moving) { answer("refused", "pi is already moving to another tab."); return; }
		let reason = refusal();
		if (reason) { answer("refused", reason); return; }
		moving = true; answer("saving");
		try { await flushVfs(); } catch { moving = false; answer("refused", SAVE_FAILED); return; }
		// A task may have started while saving.
		reason = refusal();
		if (reason) { moving = false; answer("refused", reason); return; }
		answer("accepted");
		const done = () => {
			channel.close(); release();
			showPage({
				id: "pi-instance-moved", title: "pi moved to another tab", documentTitle: TAB_TITLE,
				body: ["Your work was saved, and pi now runs in another tab of this browser. pi runs in one tab at a time."],
				actions: [{ label: "Use pi here again", action: "take-back", variant: "primary", run() {
					try { sessionStorage.setItem(ASK_ON_LOAD, "1"); } catch { /* The reloaded tab waits instead of asking. */ }
					location.reload();
				} }],
			});
		};
		if (piExited() || startupFailed()) { done(); return; }
		handOffOnExit({ done, failed() { moving = false; answer("refused", SAVE_FAILED); } });
		ctx!.shutdown();
	};
}

/**
 * Show why pi cannot start here, and resolve once this tab holds the lock: when the other tab
 * closes, or after it agreed to move pi here. Only a visible tab, or one that asked, waits in the
 * lock's queue, so a forgotten background tab does not take pi when the open tab reloads.
 */
function waitForTurn(askNow: boolean): Promise<Release> {
	return new Promise((resolve) => {
		const channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(NAME) : undefined;
		const mine = new Set<string>();
		let queued: AbortController | undefined, asking = false, timer: ReturnType<typeof setTimeout> | undefined;
		const page = showPage({
			id: "pi-instance-wait", title: "pi is open in another tab", documentTitle: TAB_TITLE,
			body: [
				"pi runs in one tab at a time, because each tab keeps its own copy of your files in memory.",
				channel
					? "This tab starts pi by itself when the other tab is closed. To move pi here now, the other tab has to be idle with its work saved."
					: "This tab starts pi by itself when the other tab is closed.",
			],
			actions: channel ? [{ label: "Use pi here", action: "take-over", variant: "primary", run: ask }] : [],
		});
		const button = page.buttons[0];

		function queue(): void {
			if (queued || (document.visibilityState === "hidden" && !asking)) return;
			const controller = queued = new AbortController();
			request({ signal: controller.signal }).then((release) => {
				if (!release) return;
				finish(); resolve(release);
			}, () => { if (queued === controller) queued = undefined; });
		}
		function unqueue(): void { queued?.abort(); queued = undefined; }
		function settle(status: string): void {
			clearTimeout(timer); asking = false; button.disabled = false; page.setStatus(status);
			// Disabling the button while asking took keyboard focus away from it.
			if (!document.activeElement || document.activeElement === document.body || document.activeElement === button) button.focus();
			if (document.visibilityState === "hidden") unqueue();
		}
		function ask(): void {
			const id = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
			mine.add(id); asking = true; button.disabled = true; page.setStatus("Asking the other tab…");
			queue();
			channel!.postMessage({ type: "request", id, sent: Date.now() } satisfies Message);
			clearTimeout(timer);
			timer = setTimeout(() => settle("The other tab did not respond. Switch to it, or close it to start pi here."), ANSWER_TIMEOUT);
		}
		const visibility = () => { if (document.visibilityState === "hidden" && !asking) unqueue(); else queue(); };
		function finish(): void {
			clearTimeout(timer); channel?.close(); document.removeEventListener("visibilitychange", visibility);
			page.close();
		}

		if (channel) channel.onmessage = ({ data }: MessageEvent<Message>) => {
			if (data?.type !== "answer") return;
			if (!mine.has(data.id)) {
				// pi is moving to another waiting tab: let it go first.
				if (data.state === "accepted" && queued) { unqueue(); queue(); }
				return;
			}
			clearTimeout(timer);
			if (data.state === "saving") page.setStatus("Saving your work in the other tab…");
			else if (data.state === "accepted") { asking = true; button.disabled = true; page.setStatus("Closing pi in the other tab…"); queue(); }
			else settle(data.reason ?? "The other tab kept pi.");
		};
		document.addEventListener("visibilitychange", visibility);
		queue();
		if (askNow && channel) ask();
	});
}

/**
 * Resolve once this tab may run pi. `waiting` runs before the page that explains why this tab
 * waits for another one.
 */
export async function acquireInstanceLock(waiting: () => void): Promise<void> {
	if (!navigator.locks) return;
	let askNow = false;
	try { askNow = sessionStorage.getItem(ASK_ON_LOAD) === "1"; sessionStorage.removeItem(ASK_ON_LOAD); } catch { /* Wait without asking. */ }
	const release = await request({ ifAvailable: true });
	if (release) { hold(release); return; }
	waiting();
	hold(await waitForTurn(askNow));
}
